import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Sparkles, Workflow, BadgeCheck, ChevronRight } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { PERSONAS } from '@/data/seed';
import BrandMark from '@/components/Brand';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { usePageTitle } from '@/lib/usePageTitle';

const HERO = 'https://customer-assets.emergentagent.com/job_5cc86f7c-55ad-4a06-8f4e-ee255a2b3366/artifacts/yo26lk9d_Alwasl_End_to_End_Collections_Platform_Prototype_BRD.docx';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/5cc86f7c-55ad-4a06-8f4e-ee255a2b3366/images/226f7eb4ec599c1af6e88095a157a53a12625b9a17966ba26ba927437e4f73ee.png';

const HIGHLIGHTS = [
{ icon: Workflow, k: 'End-to-end lifecycle', v: 'Intake → Assignment → Outreach → Payment → Legal' },
{ icon: ShieldCheck, k: 'SAMA & PDPL aligned', v: 'Contact windows, masking, consent & audit' },
{ icon: Sparkles, k: 'AI-assisted decisioning', v: 'Next-best-action, priority scoring & explainable assignment' },
{ icon: BadgeCheck, k: 'Mock Saudi ecosystem', v: 'Nafath • SIMAH • SADAD • WhatsApp • Najiz' }];


export default function Landing() {
  usePageTitle('Welcome');
  const navigate = useNavigate();
  const { setPersonaId } = useApp();

  const enter = (personaId) => {
    setPersonaId(personaId);
    if (personaId === 'agent') navigate('/app/agent');else
    if (personaId === 'legal') navigate('/app/legal');else
    if (personaId === 'compliance') navigate('/app/compliance');else
    if (personaId === 'admin') navigate('/app/integrations');else
    if (personaId === 'supervisor') navigate('/app/supervisor');else
    navigate('/app/command-center');
  };

  return (
    <div className="min-h-screen bg-onyx text-background relative overflow-hidden">
      {/* Background composition */}
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-onyx/95 via-onyx/85 to-onyx/95" />
        <div className="absolute inset-0 pattern-arabesque opacity-60" />
        <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-[360px] w-[360px] rounded-full bg-primary/10 blur-3xl" />
      </div>

      <header className="relative z-10 px-8 py-6 flex items-center justify-between">
        <BrandMark tone="ivory" />
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10">v 0.9 • Prototype</Badge>
          <Button variant="ghost" className="text-background/80 hover:text-background hover:bg-white/10" onClick={() => navigate('/app/command-center')}>
            Skip intro <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="relative z-10 px-8 pb-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 mt-6 lg:mt-12">
          {/* Left hero text */}
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.32em] text-primary mb-4">Alwasl National — Riyadh Dynamics Prototype</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              The recovery <span className="gold-text">command center</span> for modern Saudi finance.
            </h1>
            <p className="mt-6 text-lg text-background/75 max-w-2xl leading-relaxed">
              Alwasl Recovery OS unifies portfolio intake, debtor 360, intelligent assignment, omnichannel outreach,
              payment orchestration, legal escalation and regulator-grade audit — into one disciplined operating layer.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-gold" onClick={() => enter('manager')}>
                Launch executive demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="border-white/20 text-background hover:bg-white/10 hover:text-background" onClick={() => navigate('/portal')}>
                View debtor portal
              </Button>
            </div>

            <dl className="mt-12 grid sm:grid-cols-2 gap-4 max-w-2xl">
              {HIGHLIGHTS.map(({ icon: Icon, k, v }) =>
              <div key={k} className="flex gap-3 items-start p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                  <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
                  <div>
                    <p className="text-sm font-semibold">{k}</p>
                    <p className="text-xs text-background/65 mt-0.5">{v}</p>
                  </div>
                </div>
              )}
            </dl>
          </div>

          {/* Right persona picker */}
          <div className="lg:col-span-5">
            <div className="surface-card !bg-white/5 !border-white/10 backdrop-blur-md p-5 text-background">
              <p className="text-[10px] uppercase tracking-[0.28em] text-primary mb-1">Choose a persona to enter</p>
              <h3 className="text-xl font-semibold mb-1 text-background">Role-aware demo</h3>
              <p className="text-xs text-background/60 mb-4">Each persona sees a tailored workspace, KPIs, controls and audit boundary.</p>
              <div className="space-y-1.5">
                {PERSONAS.map((p) =>
                <button
                  key={p.id}
                  onClick={() => enter(p.id)}
                  className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] hover:bg-primary/10 border border-white/5 hover:border-primary/40 transition-colors text-left">

                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-primary text-primary-foreground text-[12px] font-semibold">{p.initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-background">{p.name}</p>
                      <p className="text-[11px] text-background/55 truncate">{p.user}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-background/40 group-hover:text-primary group-hover:translate-x-0.5 transition-transform" />
                  </button>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-[11px] text-background/55">
                <span>All data is synthetic</span>
                <span className="badge-mock !bg-primary/15 !border-primary/40 !text-primary">Mock integrations</span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              {[['230+', 'collectors'], ['10M', 'SAR capital'], ['3', 'branches']].map(([n, l]) =>
              <div key={l} className="py-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="font-mono-num text-lg text-primary">{n}</p>
                  <p className="text-[10px] uppercase tracking-widest text-background/55 mt-0.5">{l}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 px-8 py-5 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] text-background/50">
        <div>© Alwasl National Company for Debt Collection of Financing Entities — Prototype only, synthetic data</div>
        <div className="flex items-center gap-4">
          <span>Saudi Central Bank (SAMA) supervised</span>
          <span className="h-1 w-1 rounded-full bg-background/30" />
          <span>PDPL aware • NCA controls placeholder</span>
        </div>
      </footer>
    </div>);

}